<script lang="ts">
  // Username + password panel. Identity is anchored to Discord, but verification happens IN Discord:
  // sign up → we show a code + a "Join Discord" link → the user runs /verify <code> there → they hit
  // Continue (which just re-tries login until the account flips to verified). Password reset is the
  // mirror: run /reset in Discord, get a code, paste it here. On any success we hand the token up.
  import { fade } from "svelte/transition";
  import { registerAccount, loginAccount, confirmReset, type AuthError } from "../auth.ts";
  import Field from "./ui/Field.svelte";
  import NeonButton from "./ui/NeonButton.svelte";

  let { onToken }: { onToken: (token: string) => void } = $props();

  type Mode = "signin" | "signup" | "verify" | "reset";
  let mode = $state<Mode>("signin");
  let username = $state("");
  let password = $state("");
  let code = $state("");
  let newPassword = $state("");
  let inviteUrl = $state<string | null>(null);
  let busy = $state(false);
  let err = $state("");
  let info = $state("");
  let copied = $state(false);

  function to(next: Mode) {
    mode = next;
    err = "";
    info = "";
  }

  const needsVerification = (e: unknown) => !!(e as AuthError)?.data?.needsVerification;
  const fail = (e: unknown) => (err = (e as Error).message);

  // Register (or re-issue a code for an existing unverified account) → show the verify step.
  async function issueCode() {
    const { code: c, inviteUrl: url } = await registerAccount(username, password);
    code = c;
    inviteUrl = url;
    to("verify");
  }

  async function signin() {
    if (busy) return;
    busy = true;
    err = "";
    try {
      onToken(((await loginAccount(username, password)) as { token: string }).token);
    } catch (e) {
      if (needsVerification(e)) await issueCode();
      else fail(e);
    } finally {
      busy = false;
    }
  }

  async function signup() {
    if (busy) return;
    busy = true;
    err = "";
    try {
      await issueCode();
    } catch (e) {
      fail(e);
    } finally {
      busy = false;
    }
  }

  // After verifying in Discord: just log in again. Still unverified → tell them to finish.
  async function continueIn() {
    if (busy) return;
    busy = true;
    err = "";
    info = "";
    try {
      onToken(((await loginAccount(username, password)) as { token: string }).token);
    } catch (e) {
      if (needsVerification(e)) info = "Not verified yet, finish in Discord, then hit Continue.";
      else fail(e);
    } finally {
      busy = false;
    }
  }

  async function resetConfirm() {
    if (busy) return;
    busy = true;
    err = "";
    try {
      onToken(((await confirmReset(code, newPassword)) as { token: string }).token);
    } catch (e) {
      fail(e);
    } finally {
      busy = false;
    }
  }

  function copyCode() {
    navigator.clipboard?.writeText(code);
    copied = true;
    setTimeout(() => (copied = false), 1500);
  }

  const onEnter = (fn: () => void) => (e: KeyboardEvent) => e.key === "Enter" && fn();
</script>

<div class="space-y-3">
  {#if mode === "signin" || mode === "signup"}
    <div class="space-y-2.5">
      <Field bind:value={username} placeholder="Username" autocomplete="username" maxlength={20} onkeydown={onEnter(mode === "signin" ? signin : signup)} />
      <Field type="password" bind:value={password} placeholder="Password" autocomplete={mode === "signin" ? "current-password" : "new-password"} onkeydown={onEnter(mode === "signin" ? signin : signup)} />
    </div>

    {#if err}<p class="text-sm text-magenta" in:fade={{ duration: 150 }}>{err}</p>{/if}

    <NeonButton full onclick={mode === "signin" ? signin : signup} disabled={busy}>
      {busy ? "Working…" : mode === "signin" ? "Sign in" : "Create account"}
    </NeonButton>

    <div class="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-0.5 font-display text-xs uppercase tracking-[0.16em] text-ink-dim/85">
      {#if mode === "signin"}
        <button type="button" class="transition-colors hover:text-cyan" onclick={() => to("signup")}>Create account</button>
        <button type="button" class="transition-colors hover:text-cyan" onclick={() => to("reset")}>Reset password</button>
      {:else}
        <button type="button" class="transition-colors hover:text-cyan" onclick={() => to("signin")}>Back to sign in</button>
      {/if}
    </div>

  {:else if mode === "verify"}
    <p class="text-sm leading-relaxed text-ink-dim">
      Account created. Verify it through our Discord to finish:
    </p>

    <!-- The code to paste into Discord. -->
    <div class="flex items-center justify-between gap-3 border border-cyan-dim/40 bg-void-2/60 px-4 py-3">
      <span class="font-mono text-lg tracking-[0.3em] text-cyan">{code}</span>
      <button type="button" class="font-display text-xs uppercase tracking-[0.16em] text-ink-dim/85 transition-colors hover:text-cyan" onclick={copyCode}>
        {copied ? "Copied" : "Copy"}
      </button>
    </div>

    <ol class="space-y-1.5 text-sm leading-relaxed text-ink-dim">
      <li>1. Join our Discord server.</li>
      <li>2. In <span class="text-cyan">#verify</span>, click <span class="text-cyan">Verify my account</span> and paste your code.</li>
      <li>3. Come back here and hit Continue.</li>
    </ol>

    {#if inviteUrl}
      <a href={inviteUrl} target="_blank" rel="noopener" class="block w-full chamfer-btn bg-void-2/70 py-2.5 text-center font-display text-sm font-semibold uppercase tracking-[0.12em] text-cyan ring-1 ring-cyan/40 transition-colors hover:text-glow hover:ring-cyan" style="--c:9px">
        Join our Discord
      </a>
    {/if}

    {#if err}<p class="text-sm text-magenta" in:fade={{ duration: 150 }}>{err}</p>
    {:else if info}<p class="text-sm text-cyan" in:fade={{ duration: 150 }}>{info}</p>{/if}

    <NeonButton full onclick={continueIn} disabled={busy}>
      {busy ? "Checking…" : "I've verified, Continue"}
    </NeonButton>

    <div class="pt-0.5 text-center font-display text-xs uppercase tracking-[0.16em] text-ink-dim/85">
      <button type="button" class="transition-colors hover:text-cyan" onclick={() => to("signin")}>Back to sign in</button>
    </div>

  {:else if mode === "reset"}
    <p class="text-sm leading-relaxed text-ink-dim">
      In <span class="text-cyan">#reset-password</span> on our Discord, tap
      <span class="text-cyan">Reset password</span> for a private code, then paste it below with your
      new password.
    </p>

    <div class="space-y-2.5">
      <Field bind:value={code} placeholder="Reset code from Discord" autocomplete="off" maxlength={16} onkeydown={onEnter(resetConfirm)} />
      <Field type="password" bind:value={newPassword} placeholder="New password" autocomplete="new-password" onkeydown={onEnter(resetConfirm)} />
    </div>

    {#if err}<p class="text-sm text-magenta" in:fade={{ duration: 150 }}>{err}</p>{/if}

    <NeonButton full onclick={resetConfirm} disabled={busy}>
      {busy ? "Working…" : "Set new password"}
    </NeonButton>

    <div class="pt-0.5 text-center font-display text-xs uppercase tracking-[0.16em] text-ink-dim/85">
      <button type="button" class="transition-colors hover:text-cyan" onclick={() => to("signin")}>Back to sign in</button>
    </div>
  {/if}
</div>
